import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// ══════════════════════════════════════════════════════
// DEVICE CONFIG
// ══════════════════════════════════════════════════════

const PORT_STATUSES = ['Not In Use', 'In Use', 'Disabled'];
const PORT_COLORS   = { 'In Use':'badge-completed', 'Not In Use':'badge-pending', 'Disabled':'badge-faulty' };

// ─── Dynamic field renderer ────────────────────────────────────────────────
function DynamicField({ field, value, onChange, readOnly }) {
  const base = readOnly ? { background:'var(--bg3)', cursor:'default' } : {};
  if (field.type === 'textarea') return (
    <div className="form-group" key={field.key}>
      <label>{field.label}{field.required ? ' *' : ''}</label>
      <textarea rows={3} value={value||''} onChange={e=>onChange(field.key,e.target.value)} readOnly={readOnly} style={base} />
    </div>
  );
  return (
    <div className="form-group" key={field.key}>
      <label>{field.label}{field.required ? ' *' : ''}</label>
      <input type={field.type==='number'?'number':'text'}
        value={value||''} onChange={e=>onChange(field.key,e.target.value)}
        readOnly={readOnly}
        style={{ ...base, ...(field.type==='password'?{fontFamily:'monospace'}:{}) }} />
    </div>
  );
}

// ─── Port Table ────────────────────────────────────────────────────────────
function PortTable({ ports, onChange, readOnly }) {
  if (!ports.length) return <div style={{ color:'var(--text2)', fontSize:13, padding:8 }}>No ports</div>;
  return (
    <div className="table-wrap" style={{ maxHeight:420, overflowY:'auto' }}>
      <table>
        <thead>
          <tr>
            <th style={{ width:70 }}>Port</th>
            <th style={{ width:90 }}>Name</th>
            <th style={{ width:130 }}>Status</th>
            <th style={{ width:70 }}>VLAN</th>
            <th>Connected To</th>
            <th>Location</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {ports.map((p, i) => (
            <tr key={p.port_number} style={{ background: p.status==='In Use'?'rgba(52,211,153,0.04)':p.status==='Disabled'?'rgba(248,113,113,0.04)':'' }}>
              <td><span className="mono" style={{ fontSize:12, color:'var(--text2)' }}>Port {p.port_number}</span></td>
              <td>{readOnly
                ? <span style={{ fontSize:12 }}>{p.port_name||'—'}</span>
                : <input value={p.port_name||''} onChange={e=>onChange(i,'port_name',e.target.value)} style={{ padding:'3px 6px',fontSize:12,width:'100%' }} placeholder="LAN1" />}
              </td>
              <td>{readOnly
                ? <span className={`badge ${PORT_COLORS[p.status]||''}`}>{p.status}</span>
                : <select value={p.status||'Not In Use'} onChange={e=>onChange(i,'status',e.target.value)} style={{ fontSize:12,width:'100%' }}>
                    {PORT_STATUSES.map(s=><option key={s}>{s}</option>)}
                  </select>}
              </td>
              <td>{readOnly
                ? <span style={{ fontSize:12 }}>{p.vlan||'—'}</span>
                : <input value={p.vlan||''} onChange={e=>onChange(i,'vlan',e.target.value)} style={{ padding:'3px 6px',fontSize:12,width:'100%' }} placeholder="10" />}
              </td>
              <td>{readOnly
                ? <span style={{ fontSize:12 }}>{p.connected_to||'—'}</span>
                : <input value={p.connected_to||''} onChange={e=>onChange(i,'connected_to',e.target.value)} style={{ padding:'3px 6px',fontSize:12,width:'100%' }} />}
              </td>
              <td>{readOnly
                ? <span style={{ fontSize:12 }}>{p.location||'—'}</span>
                : <input value={p.location||''} onChange={e=>onChange(i,'location',e.target.value)} style={{ padding:'3px 6px',fontSize:12,width:'100%' }} />}
              </td>
              <td>{readOnly
                ? <span style={{ fontSize:12,color:'var(--text2)' }}>{p.remarks||'—'}</span>
                : <input value={p.remarks||''} onChange={e=>onChange(i,'remarks',e.target.value)} style={{ padding:'3px 6px',fontSize:12,width:'100%' }} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Config Detail / Edit ──────────────────────────────────────────────────
function ConfigDetail({ configId, onBack, onSaved }) {
  const { isAdmin } = useAuth();
  const [cfg,   setCfg]   = useState(null);
  const [ports, setPorts] = useState([]);
  const [data,  setData]  = useState({});
  const [name,  setName]  = useState('');
  const [edit,  setEdit]  = useState(false);
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/configs/${configId}`);
      const c = r.data;
      setCfg(c); setData(c.config_data||{}); setName(c.device_name||'');
      const total = c.port_count || c.dt_port_count || 0;
      const existing = c.ports || [];
      setPorts(Array.from({ length: total }, (_, i) => {
        const ex = existing.find(p => p.port_number === i+1);
        return ex || { port_number:i+1, port_name:'', status:'Not In Use', vlan:'', connected_to:'', location:'', remarks:'' };
      }));
    } catch(e) { setErr('Failed to load'); }
  }, [configId]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, val) => setData(d => ({ ...d, [key]: val }));
  const setPort  = (i, key, val) => setPorts(p => p.map((x,idx) => idx===i ? { ...x, [key]:val } : x));

  const save = async () => {
    const fields = cfg?.device_type_fields || [];
    for (const f of fields) {
      if (f.required && !data[f.key]?.trim()) { setErr(`${f.label} is required`); return; }
    }
    setBusy(true); setErr('');
    try {
      await api.put(`/configs/${configId}`, { device_name:name, config_data:data, port_count:ports.length, ports });
      setEdit(false); load(); onSaved?.();
    } catch(e) { setErr(e.response?.data?.error||'Save failed'); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>;
  const fields = cfg.device_type_fields || [];
  const inUse  = ports.filter(p=>p.status==='In Use').length;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <div style={{ flex:1 }}>
          <div className="flex-gap" style={{ gap:10, flexWrap:'wrap' }}>
            <span className="mono" style={{ color:'var(--accent)', fontWeight:700, fontSize:15 }}>{cfg.asset_tag}</span>
            <span style={{ fontWeight:500 }}>{cfg.device_type_name}</span>
            {cfg.device_name && <span style={{ color:'var(--text2)', fontSize:13 }}>"{cfg.device_name}"</span>}
          </div>
          <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>
            {cfg.brand} {cfg.model}{cfg.serial_number ? ` · S/N: ${cfg.serial_number}` : ''} · {cfg.campus_name}
            {cfg.asset_room_name && ` · 📍 ${cfg.asset_room_name}`}
          </div>
        </div>
        {isAdmin && !edit && <button className="btn btn-secondary" onClick={()=>setEdit(true)}>Edit Config</button>}
        {isAdmin && edit && <>
          <button className="btn btn-secondary" onClick={()=>{setEdit(false);load();}}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save Config'}</button>
        </>}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 260px', gap:20, alignItems:'start' }}>
        <div>
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Configuration</div>
            {edit && (
              <div className="form-group">
                <label>Device Label</label>
                <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Main Firewall" />
              </div>
            )}
            {!edit && name && (
              <div style={{ marginBottom:14, paddingBottom:10, borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontSize:11, color:'var(--text2)' }}>Device Label</div>
                <div style={{ fontWeight:600, marginTop:3 }}>{name}</div>
              </div>
            )}
            {fields.length===0 && <div style={{ color:'var(--text2)', fontSize:13 }}>No config fields defined.</div>}
            {fields.length>0 && (
              <div style={{ display:'grid', gridTemplateColumns: edit?'1fr':'1fr 1fr', gap: edit?0:'4px 20px' }}>
                {fields.map(f => edit
                  ? <DynamicField key={f.key} field={f} value={data[f.key]} onChange={setField} readOnly={false} />
                  : <div key={f.key} style={{ padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                      <div style={{ fontSize:11, color:'var(--text2)' }}>{f.label}</div>
                      <div style={{ fontWeight:500, fontSize:13, marginTop:2, fontFamily:f.type==='password'?'monospace':'inherit' }}>
                        {f.type==='password'&&data[f.key] ? '••••••••' : (data[f.key]||'—')}
                      </div>
                    </div>
                )}
              </div>
            )}
            {err && <div className="err-msg" style={{ marginTop:10 }}>{err}</div>}
          </div>

          {ports.length>0 && (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Ports — {ports.length} total
                </div>
                <div className="flex-gap" style={{ gap:6 }}>
                  <span className="badge badge-completed">{inUse} In Use</span>
                  <span className="badge badge-pending">{ports.length - inUse - ports.filter(p=>p.status==='Disabled').length} Free</span>
                  {ports.filter(p=>p.status==='Disabled').length>0 && <span className="badge badge-faulty">{ports.filter(p=>p.status==='Disabled').length} Disabled</span>}
                </div>
              </div>
              <PortTable ports={ports} onChange={setPort} readOnly={!edit} />
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ fontWeight:600, fontSize:13, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Asset</div>
          <div className="mono" style={{ color:'var(--accent)', fontWeight:700, fontSize:14, marginBottom:8 }}>{cfg.asset_tag}</div>
          {[['Brand',cfg.brand],['Model',cfg.model],['Serial',cfg.serial_number],['Type',cfg.device_type_name],['Condition',cfg.asset_condition],['Room',cfg.asset_room_name],['Staff',cfg.asset_staff_name],['Campus',cfg.campus_name]]
            .filter(([,v])=>v).map(([l,v])=>(
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
              <span style={{ color:'var(--text2)' }}>{l}</span><span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Add Config Modal ──────────────────────────────────────────────────────
function ConfigModal({ onClose, onSaved }) {
  const { user, isAdmin } = useAuth();
  const [campuses,    setCampuses]    = useState([]);
  const [assets,      setAssets]      = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [form,        setForm]        = useState({ campus_id: isAdmin?'':String(user.campus_ids?.[0]||''), asset_id:'', device_name:'' });
  const [assetType,   setAssetType]   = useState(null);
  const [configData,  setConfigData]  = useState({});
  const [portCount,   setPortCount]   = useState(0);
  const [busy,        setBusy]        = useState(false);
  const [err,         setErr]         = useState('');

  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data));
    api.get('/master/device-types').then(r => setDeviceTypes(r.data));
  }, []);

  useEffect(() => {
    if (form.campus_id) {
      api.get('/assets', { params: { campus_id: form.campus_id } }).then(r => {
        const configTypes = new Set(deviceTypes.filter(dt => dt.config_required && dt.active).map(dt => dt.name));
        setAssets(r.data.filter(a => configTypes.has(a.asset_type)));
      });
    } else { setAssets([]); setAssetType(null); setConfigData({}); }
  }, [form.campus_id, deviceTypes]);

  useEffect(() => {
    if (form.asset_id) {
      const asset = assets.find(a => String(a.id) === form.asset_id);
      if (asset) {
        const dt = deviceTypes.find(dt => dt.name === asset.asset_type);
        setAssetType(dt||null); setConfigData({}); setPortCount(dt?.port_count||0);
      }
    } else { setAssetType(null); setConfigData({}); setPortCount(0); }
  }, [form.asset_id, assets, deviceTypes]);

  const setField = (key, val) => setConfigData(d => ({ ...d, [key]: val }));

  const save = async () => {
    if (!form.campus_id) { setErr('Campus required'); return; }
    if (!form.asset_id)  { setErr('Asset required'); return; }
    if (assetType) {
      for (const f of (assetType.fields||[])) {
        if (f.required && !configData[f.key]?.trim()) { setErr(`${f.label} is required`); return; }
      }
    }
    setBusy(true); setErr('');
    try {
      await api.post('/configs', { campus_id:parseInt(form.campus_id), asset_id:parseInt(form.asset_id), device_type_id:assetType?.id||null, device_name:form.device_name||null, config_data:configData, port_count:portCount });
      onSaved(); onClose();
    } catch(e) { setErr(e.response?.data?.error||'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:580 }}>
        <div className="modal-header">
          <span className="modal-title">Add Device Configuration</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Campus *</label>
            <select value={form.campus_id} onChange={e=>setForm(f=>({...f,campus_id:e.target.value,asset_id:''}))}>
              <option value="">— Select —</option>
              {campuses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Asset * <span style={{ fontSize:11,color:'var(--text2)' }}>(network devices)</span></label>
            <select value={form.asset_id} onChange={e=>setForm(f=>({...f,asset_id:e.target.value}))}>
              <option value="">— Select asset —</option>
              {assets.map(a=><option key={a.id} value={a.id}>{a.asset_tag} · {a.asset_type} · {a.brand} {a.model}</option>)}
            </select>
            {form.campus_id && assets.length===0 && deviceTypes.length>0 && (
              <div style={{ fontSize:12,color:'var(--amber)',marginTop:4 }}>No configurable devices in this campus. Add Firewall/Switch/Router etc. assets first.</div>
            )}
          </div>
        </div>

        {assetType && (
          <>
            <div style={{ background:'rgba(79,142,247,0.08)', border:'1px solid rgba(79,142,247,0.2)', borderRadius:'var(--radius)', padding:'8px 12px', marginBottom:14, fontSize:12 }}>
              <strong style={{ color:'var(--accent)' }}>{assetType.name}</strong>
              {assetType.port_based && <> · Port-based · Default {assetType.port_count} ports</>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Device Label <span style={{ fontSize:11,color:'var(--text2)' }}>(optional)</span></label>
                <input value={form.device_name} onChange={e=>setForm(f=>({...f,device_name:e.target.value}))} placeholder="e.g. Core Switch 1" />
              </div>
              {assetType.port_based && (
                <div className="form-group">
                  <label>Port Count</label>
                  <input type="number" value={portCount} min={1} max={256} onChange={e=>setPortCount(parseInt(e.target.value)||assetType.port_count)} style={{ maxWidth:90 }} />
                </div>
              )}
            </div>
            {(assetType.fields||[]).map(f=>(
              <DynamicField key={f.key} field={f} value={configData[f.key]} onChange={setField} readOnly={false} />
            ))}
          </>
        )}

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Add Config'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Config List Page ──────────────────────────────────────────────────────
export function Configs() {
  const { isAdmin, can } = useAuth();
  const [configs,  setConfigs]  = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [filters,  setFilters]  = useState({ search:'', campus_id:'' });
  const [modal,    setModal]    = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)    params.search    = filters.search;
      if (filters.campus_id) params.campus_id = filters.campus_id;
      const r = await api.get('/configs', { params });
      setConfigs(r.data);
    } catch(e) {}
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data)); }, []);

  if (detailId) return <ConfigDetail configId={detailId} onBack={()=>setDetailId(null)} onSaved={load} />;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>Device Configuration</h2>
        {isAdmin && <button className="btn btn-primary" onClick={()=>setModal(true)}>+ Add Config</button>}
      </div>

      <div className="card" style={{ marginBottom:16, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
          <input placeholder="Search by asset tag, device type, IP, hostname…"
            value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))} />
          {isAdmin && (
            <select value={filters.campus_id} onChange={e=>setFilters(f=>({...f,campus_id:e.target.value}))}>
              <option value="">All Campuses</option>
              {campuses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !configs.length ? <div className="empty-state"><h3>No device configurations</h3><p>Add network device assets first, then configure them here.</p></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Asset Tag</th><th>Device Type</th><th>Label</th><th>Key Info</th><th>Ports</th><th>Campus / Location</th><th>Action</th></tr>
              </thead>
              <tbody>
                {configs.map(c => {
                  const cd = c.config_data || {};
                  const keyInfo = cd.ip_address || cd.management_ip || cd.lan_ip || cd.hostname || '—';
                  return (
                    <tr key={c.id} style={{ cursor:'pointer' }} onClick={()=>setDetailId(c.id)}>
                      <td>
                        <span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{c.asset_tag||'—'}</span>
                        <div style={{ fontSize:11, color:'var(--text2)' }}>{c.brand} {c.model}</div>
                      </td>
                      <td><span style={{ fontSize:13 }}>{c.device_type_name||'—'}</span></td>
                      <td><span style={{ fontSize:13, color:'var(--text2)' }}>{c.device_name||'—'}</span></td>
                      <td><span className="mono" style={{ fontSize:12 }}>{keyInfo}</span></td>
                      <td>{c.port_based ? <span style={{ fontSize:12 }}>{c.ports_in_use||0} / {c.port_count||0} in use</span> : <span style={{ color:'var(--text2)',fontSize:12 }}>N/A</span>}</td>
                      <td>
                        <div style={{ fontSize:12 }}>{c.campus_name}</div>
                        {c.asset_room_name && <div style={{ fontSize:11, color:'var(--text2)' }}>📍 {c.asset_room_name}</div>}
                      </td>
                      <td onClick={e=>e.stopPropagation()}>
                        <button className="btn btn-secondary btn-sm" onClick={()=>setDetailId(c.id)}>View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <ConfigModal onClose={()=>setModal(false)} onSaved={load} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// SCRAP
// ══════════════════════════════════════════════════════
export function Scrap() {
  const { user, isAdmin, can } = useAuth();
  const [records,  setRecords]  = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [filters,  setFilters]  = useState({ search:'', campus_id:'' });
  const [modal,    setModal]    = useState(false);
  const [form,     setForm]     = useState({});
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState('');
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)    params.search    = filters.search;
      if (filters.campus_id) params.campus_id = filters.campus_id;
      const r = await api.get('/scrap', { params });
      setRecords(r.data);
    } catch(e) {}
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data)); }, []);

  const openAdd = () => {
    setForm({ campus_id: isAdmin?'':String(user.campus_ids?.[0]||''), device_name:'', asset_tag:'', serial_number:'', scrap_date:'', reason:'', authorized_by:'', disposal_method:'', remarks:'' });
    setErr(''); setModal(true);
  };

  const save = async () => {
    if (!form.campus_id || !form.device_name) { setErr('Campus and device name required'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/scrap', { ...form, campus_id: parseInt(form.campus_id) });
      setModal(false); load();
    } catch(e) { setErr(e.response?.data?.error||'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>Scrap Register</h2>
        {can('scrap','create') && <button className="btn btn-primary" onClick={openAdd}>+ Add Scrap Record</button>}
      </div>

      <div className="card" style={{ marginBottom:16, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
          <input placeholder="Search by asset tag, device name, serial…"
            value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))} />
          {isAdmin && (
            <select value={filters.campus_id} onChange={e=>setFilters(f=>({...f,campus_id:e.target.value}))}>
              <option value="">All Campuses</option>
              {campuses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !records.length ? <div className="empty-state"><h3>No scrap records</h3></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Scrap ID</th><th>Device</th><th>Asset Tag</th><th>Serial</th><th>Campus</th><th>Scrap Date</th><th>Reason</th><th>Authorized By</th></tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td><span className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{r.scrap_id}</span></td>
                    <td style={{ fontWeight:500 }}>{r.device_name}</td>
                    <td><span className="mono" style={{ fontSize:12 }}>{r.asset_tag||'—'}</span></td>
                    <td><span className="mono" style={{ fontSize:12 }}>{r.serial_number||'—'}</span></td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{r.campus_name}</span></td>
                    <td><span style={{ fontSize:12 }}>{r.scrap_date ? new Date(r.scrap_date).toLocaleDateString() : '—'}</span></td>
                    <td><span style={{ fontSize:12, color:'var(--text2)' }}>{r.reason||'—'}</span></td>
                    <td><span style={{ fontSize:12 }}>{r.authorized_by||'—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header">
              <span className="modal-title">Add Scrap Record</span>
              <button className="modal-close" onClick={()=>setModal(false)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Campus *</label>
                <select value={form.campus_id} onChange={e=>setForm(f=>({...f,campus_id:e.target.value}))} disabled={!isAdmin&&user?.campus_ids?.length===1}>
                  <option value="">— Select —</option>
                  {campuses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Device Name *</label><input value={form.device_name} onChange={e=>setForm(f=>({...f,device_name:e.target.value}))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Asset Tag</label><input className="mono" value={form.asset_tag} onChange={e=>setForm(f=>({...f,asset_tag:e.target.value}))} /></div>
              <div className="form-group"><label>Serial Number</label><input className="mono" value={form.serial_number} onChange={e=>setForm(f=>({...f,serial_number:e.target.value}))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Scrap Date</label><input type="date" value={form.scrap_date} onChange={e=>setForm(f=>({...f,scrap_date:e.target.value}))} /></div>
              <div className="form-group"><label>Authorized By</label><input value={form.authorized_by} onChange={e=>setForm(f=>({...f,authorized_by:e.target.value}))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Disposal Method</label><input value={form.disposal_method} onChange={e=>setForm(f=>({...f,disposal_method:e.target.value}))} /></div>
              <div className="form-group"><label>Reason</label><input value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Remarks</label><input value={form.remarks} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} /></div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Add Record'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Configs;
